import {
  OPERATION_KIND,
  RENEWAL_DISCOUNT_STATE,
  formatParisDate,
  parisDayKey,
  type OperationDetail,
  type RenewalDiscountDetail,
  type RenewalDiscountOperationInput,
} from '@flux/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Ellipsis, Power, RotateCw, Undo2, Zap } from 'lucide-react';
import { useEffect, useId, useRef, useState, type JSX } from 'react';
import { useUnsavedChangesGuard } from '@/components/forms/unsaved-changes-guard.js';
import { Page, PageHeader } from '@/components/layout/page.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { ModeBanner } from '../subscriptions/recharge-status.js';
import { formatCount, formatCountLabel } from '../subscriptions/money.js';
import {
  activateRenewal,
  archiveOperation,
  deactivateRenewal,
  removeRenewalDiscounts,
  rerunRenewalInitial,
  startRenewalPreview,
  updateOperation,
} from './api.js';
import { ArchivedBanner, archiveDescription, GatedButton, mutationErrorMessage } from './operation-ui.js';
import {
  durationLabel,
  lastPreview,
  RenewalActivateDialog,
  SECONDS_PER_CUSTOMER,
} from './renewal-discount-activate-dialog.js';
import { RenewalDiscountForm } from './renewal-discount-form.js';
import { RenewalPreviewTab } from './renewal-discount-preview-tab.js';
import { RenewalTrackingTab } from './renewal-discount-tracking-tab.js';

export type RenewalTab = 'reglages' | 'apercu' | 'suivi';

const OPERATIONS_LIST_SEARCH = { page: 1, pageSize: 50, sortDir: 'desc' } as const;

/** Clients portant une remise posée chez Recharge (ce que « Retirer toutes les remises » touche). */
function carryingCount(config: RenewalDiscountDetail): number {
  return (config.counts.applied ?? 0) + (config.counts.replaced ?? 0);
}

function removeLabel(count: number): string {
  if (count === 0) {
    return 'Retirer les remises';
  }
  return count === 1 ? 'Retirer la remise' : `Retirer les ${formatCount(count)} remises`;
}

type PendingAction = 'activate' | 'rerun' | 'remove' | 'archive' | null;

export interface RenewalDiscountPageProps {
  detail: OperationDetail;
  config: RenewalDiscountDetail;
  tab: RenewalTab;
  onTabChange: (tab: RenewalTab) => void;
}

/**
 * Fiche d'une remise sur le prochain renouvellement (`07` §6) : Réglages,
 * Aperçu (recalculable) et Suivi des clients.
 *
 * Actions : « Activer… » (confirmation renforcée), « Relancer la passe
 * initiale… » (confirmation qui rappelle la durée et l'écriture chez
 * Recharge), « Retirer toutes les remises… » (menu `⋯`, saisie du nom, action
 * définitive). « Désactiver » est immédiat et réversible : toast avec
 * « Réactiver ». Les erreurs s'affichent dans la surface qui a lancé l'action.
 */
export function RenewalDiscountPage({ detail, config, tab, onTabChange }: RenewalDiscountPageProps): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingAction>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [formDirty, setFormDirty] = useState(false);
  const guard = useUnsavedChangesGuard(formDirty);
  const reasonIds = useId();
  const id = detail.id;
  // État lu par les rappels qui survivent au rendu (toast « Réactiver », fin d'une mutation).
  const mountedRef = useRef(true);
  const reactivateToastRef = useRef<string | number | null>(null);
  const reactivateRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // « Réactiver » n'a plus de sens une fois la fiche quittée.
      if (reactivateToastRef.current !== null) {
        toast.dismiss(reactivateToastRef.current);
      }
    };
  }, []);

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: queryKeys.operations.all });
  }

  function openDialog(action: Exclude<PendingAction, null>): void {
    activate.reset();
    initial.reset();
    remove.reset();
    archive.reset();
    setPending(action);
  }

  const save = useMutation({
    mutationFn: (input: RenewalDiscountOperationInput) => updateOperation(id, input),
    onSuccess: (saved) => {
      toast.success('Opération enregistrée.');
      queryClient.setQueryData(queryKeys.operations.detail(saved.id), saved);
      refresh();
    },
  });
  const preview = useMutation({
    mutationFn: () => startRenewalPreview(id),
    onSuccess: refresh,
  });
  const activate = useMutation({
    mutationFn: () => activateRenewal(id, {}),
    onSuccess: () => {
      toast.success('Opération activée : passe initiale lancée.');
      setPending(null);
      setPageError(null);
      refresh();
      if (mountedRef.current) {
        onTabChange('suivi');
      }
    },
  });
  const initial = useMutation({
    mutationFn: () => rerunRenewalInitial(id),
    onSuccess: () => {
      toast.success('Passe initiale relancée.');
      setPending(null);
      refresh();
    },
  });
  const deactivate = useMutation({
    mutationFn: () => deactivateRenewal(id),
    onSuccess: () => {
      setPageError(null);
      reactivateToastRef.current = toast.success('Opération désactivée', {
        action: {
          label: 'Réactiver',
          onClick: () => {
            reactivateToastRef.current = null;
            reactivateRef.current();
          },
        },
      });
      refresh();
    },
    onError: (error: unknown) => {
      setPageError(`Désactivation impossible : ${mutationErrorMessage(error)}`);
    },
  });
  const remove = useMutation({
    mutationFn: () => removeRenewalDiscounts(id),
    onSuccess: () => {
      toast.success('Retrait des remises lancé.');
      setPending(null);
      refresh();
      // Avec des réglages en cours de saisie, on reste sur le formulaire.
      if (!formDirty && mountedRef.current) {
        onTabChange('suivi');
      }
    },
  });
  const archive = useMutation({
    mutationFn: () => archiveOperation(id),
    onSuccess: () => {
      toast.success('Opération archivée.');
      setPending(null);
      refresh();
      guard.allowNextNavigation();
      void navigate({ to: '/operations', search: OPERATIONS_LIST_SEARCH });
    },
  });

  /**
   * « Réactiver » du toast : même endpoint que l'activation, mêmes gardes.
   * L'échec reste affiché en bannière.
   */
  function reactivate(): void {
    if (activateReason !== null) {
      setPageError(`Réactivation impossible : ${activateReason}`);
      return;
    }
    activate.mutate(undefined, {
      onError: (error: unknown) => {
        setPageError(`Réactivation impossible : ${mutationErrorMessage(error)}`);
      },
    });
  }

  const running = config.lastRuns.some((run) => run.status === 'running' && run.kind !== 'preview');
  const canActivate = !detail.archived && (config.state === 'draft' || config.state === 'ended');
  const isActive = config.state === 'active';
  const canRemove = config.state === 'active' || config.state === 'ended';
  const carrying = carryingCount(config);
  const lastPreviewCustomers = lastPreview(config)?.customersWithCharge ?? null;

  // `07` §6 : activation refusée une fois la période terminée (jours de Paris).
  const periodEnded = parisDayKey(new Date()) > config.periodDays.end;
  const activateReason =
    config.unavailableReason ??
    (periodEnded
      ? 'La période est terminée.'
      : formDirty
        ? 'Enregistrez d’abord vos modifications.'
        : running
          ? 'Une exécution est en cours.'
          : null);
  reactivateRef.current = reactivate;
  const rerunReason = running ? 'Une exécution est en cours.' : null;
  const removeReason =
    config.unavailableReason ?? (running ? 'Une exécution est en cours.' : null);

  // Une ligne visible sous les actions : la raison de la plus importante (Activer, sinon Relancer).
  const headerReason = canActivate ? activateReason : isActive ? rerunReason : null;

  function dialogError(mutationError: unknown, isError: boolean): string | null {
    return isError ? mutationErrorMessage(mutationError) : null;
  }

  return (
    <Page width="detail">
      <PageHeader
        backLink={{ label: 'Retour aux opérations', to: '/operations', search: OPERATIONS_LIST_SEARCH }}
        title={detail.name}
        status={
          <span className="flex flex-wrap items-center gap-1.5">
            <StatusBadge registry={RENEWAL_DISCOUNT_STATE} code={config.state} />
            <StatusBadge registry={OPERATION_KIND} code={detail.kind} />
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="tabular-nums">
              {`Renouvellements du ${formatParisDate(new Date(detail.period.start))}${
                detail.period.end === null ? '' : ` au ${formatParisDate(new Date(detail.period.end))}`
              }`}
            </span>
            {config.state === 'draft' ? null : (
              <>
                <span aria-hidden="true">·</span>
                <span className="tabular-nums">{formatCountLabel(carrying, 'remise en place', 'remises en place')}</span>
              </>
            )}
          </span>
        }
        actions={
          <span className="flex flex-col items-end gap-1.5">
          <span className="flex flex-wrap items-center justify-end gap-2">
            {isActive ? (
              <>
                <GatedButton
                  variant="outline"
                  reason={rerunReason}
                  reasonId={`${reasonIds}-rerun`}
                  onClick={() => {
                    openDialog('rerun');
                  }}
                >
                  <RotateCw aria-hidden="true" />
                  Relancer la passe initiale…
                </GatedButton>
                <Button
                  type="button"
                  variant="outline"
                  isLoading={deactivate.isPending}
                  onClick={() => {
                    deactivate.mutate();
                  }}
                >
                  <Power aria-hidden="true" />
                  Désactiver
                </Button>
              </>
            ) : null}
            {canActivate ? (
              <GatedButton
                reason={activateReason}
                reasonId={`${reasonIds}-activate`}
                onClick={() => {
                  openDialog('activate');
                }}
              >
                <Zap aria-hidden="true" />
                Activer…
              </GatedButton>
            ) : null}
            {canRemove ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="icon" aria-label="Autres actions">
                    <Ellipsis aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuItem
                    disabled={removeReason !== null}
                    onSelect={() => {
                      openDialog('remove');
                    }}
                  >
                    <span className="flex flex-col gap-0.5">
                      <span className="flex items-center gap-2">
                        <Undo2 aria-hidden="true" className="size-4" />
                        Retirer toutes les remises…
                      </span>
                      {removeReason === null ? null : <span className="text-xs text-muted-foreground">{removeReason}</span>}
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </span>
          {/* Doublon visible de la raison déjà reliée au bouton (aria-describedby) : masqué aux lecteurs d'écran. */}
          {headerReason === null ? null : (
            <span className="text-xs text-muted-foreground" aria-hidden="true">
              {headerReason}
            </span>
          )}
          </span>
        }
      />

      {detail.archived ? <ArchivedBanner kind="renewal_discount" /> : null}

      {pageError === null ? null : (
        <div
          role="alert"
          className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-status-red-border bg-status-red-bg px-3 py-2 text-sm text-status-red-fg"
        >
          <p className="min-w-0 flex-1">{pageError}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setPageError(null);
            }}
          >
            Masquer
          </Button>
        </div>
      )}

      {config.unavailableReason === null ? null : (
        <ModeBanner mode="disabled" copy={{ title: 'Aucune écriture chez Recharge', body: config.unavailableReason }} />
      )}

      {/* Activation manuelle : le focus rendu à un onglet (fermeture de la garde de sortie) ne doit pas le sélectionner. */}
      <Tabs
        activationMode="manual"
        value={tab}
        onValueChange={(next) => {
          onTabChange(next === 'apercu' || next === 'suivi' ? next : 'reglages');
        }}
      >
        <TabsList>
          <TabsTrigger value="reglages">Réglages</TabsTrigger>
          <TabsTrigger value="apercu">Aperçu</TabsTrigger>
          <TabsTrigger value="suivi">Suivi</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'apercu' ? (
        <RenewalPreviewTab
          detail={detail}
          config={config}
          isStarting={preview.isPending}
          startError={dialogError(preview.error, preview.isError)}
          onStart={() => {
            preview.mutate();
          }}
        />
      ) : tab === 'suivi' ? (
        <RenewalTrackingTab operationId={id} config={config} />
      ) : (
        <RenewalDiscountForm
          detail={detail}
          isSaving={save.isPending}
          submitError={dialogError(save.error, save.isError)}
          onDirtyChange={setFormDirty}
          onSubmit={(input) => {
            save.mutate(input);
          }}
          {...(detail.archived || isActive ? {} : { onArchive: (): void => { openDialog('archive'); } })}
        />
      )}

      <RenewalActivateDialog
        open={pending === 'activate'}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        detail={detail}
        config={config}
        isConfirming={activate.isPending}
        error={pending === 'activate' ? dialogError(activate.error, activate.isError) : null}
        onConfirm={() => {
          activate.mutate();
        }}
        onRecomputePreview={() => {
          setPending(null);
          preview.mutate();
          onTabChange('apercu');
        }}
      />
      <ConfirmDialog
        open={pending === 'rerun'}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={`Relancer la passe initiale de « ${detail.name} » ?`}
        description={
          <>
            <p>
              Flux relit tous les renouvellements de la période et pose chez Recharge les remises manquantes. Les remises déjà
              posées ne sont pas dupliquées.
            </p>
            <p className="mt-2">
              {lastPreviewCustomers === null
                ? `Durée estimée : environ ${String(SECONDS_PER_CUSTOMER)} s par client ; aucun aperçu calculé, le nombre de clients n’est pas connu.`
                : `Durée estimée : ${durationLabel(lastPreviewCustomers)} (≈ ${String(SECONDS_PER_CUSTOMER)} s par client, ${formatCountLabel(
                    lastPreviewCustomers,
                    'client',
                    'clients',
                  )} au dernier aperçu).`}
            </p>
          </>
        }
        confirmLabel="Relancer la passe initiale"
        confirmVariant="default"
        isConfirming={initial.isPending}
        error={pending === 'rerun' ? dialogError(initial.error, initial.isError) : null}
        onConfirm={() => {
          initial.mutate();
        }}
      />
      <ConfirmDialog
        open={pending === 'remove'}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={`Retirer les remises de « ${detail.name} » ?`}
        description={
          <>
            <p>
              {carrying === 0
                ? 'Aucun renouvellement en attente ne porte la remise : Flux désactive tous les codes de l’opération.'
                : `Flux retire la remise ${carrying === 1 ? 'du renouvellement en attente qui la porte' : `des ${formatCount(carrying)} renouvellements en attente qui la portent`} et désactive tous les codes de l’opération : ces clients repasseront au plein tarif. Les codes clients remplacés ne sont pas reposés.`}
            </p>
            <p className="mt-2 font-medium text-foreground">Action définitive : l’opération ne pourra plus être réactivée.</p>
          </>
        }
        verificationText={detail.name}
        confirmLabel={removeLabel(carrying)}
        isConfirming={remove.isPending}
        error={pending === 'remove' ? dialogError(remove.error, remove.isError) : null}
        onConfirm={() => {
          remove.mutate();
        }}
      />
      <ConfirmDialog
        open={pending === 'archive'}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={`Archiver « ${detail.name} » ?`}
        description={archiveDescription('renewal_discount', config.state === 'ended' ? carrying : 0)}
        confirmLabel="Archiver l’opération"
        isConfirming={archive.isPending}
        error={pending === 'archive' ? dialogError(archive.error, archive.isError) : null}
        onConfirm={() => {
          archive.mutate();
        }}
      />
      {guard.dialog}
    </Page>
  );
}

